import { useLazyQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'

import { SelectTags } from 'components/v2/SelectTags'
import { ISelectTagsProps } from 'components/v2/SelectTags/types'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GTSegmentSuggestion, GTSegmentSuggestionsResponse } from 'gql/types.generated'
import { GET_SEGMENTS_SUGGESTIONS } from 'features/Segments/queries-legacy'

interface IMultiSegmentAutocompleteProps extends Omit<ISelectTagsProps, 'onChange' | 'items' | 'getTagLabel'> {
  onChange: (segments: GTSegmentSuggestion[]) => void
  originIds?: string[]
  destinationIds?: string[]
  onlyNoFareFamily?: boolean
  companyId?: string
  skip?: boolean
}

const getSegmentLabel = (segment: GTSegmentSuggestion): string => {
  if (segment.name) {
    return segment.name
  }
  return `${segment.origin.code} - ${segment.destination.code}`
}

export const MultiSegmentAutocomplete = ({
  onChange,
  label,
  placeholder,
  values,
  companyId,
  originIds,
  destinationIds,
  onlyNoFareFamily,
  skip,
  ...props
}: IMultiSegmentAutocompleteProps): ReactElement => {
  const [pattern, setPattern] = useState('')
  const [getSegments, { data, loading, error }] = useLazyQuery<{
    segmentSuggestions: GTSegmentSuggestionsResponse
  }>(GET_SEGMENTS_SUGGESTIONS)
  const { isDebouncing } = useDebouncedQuery({
    query: getSegments,
    pattern,
    extraInputs: {
      companyID: companyId || null,
      originCityIDs: originIds,
      destinationCityIDs: destinationIds,
      onlyNoFareFamily
    },
    skip
  })

  const shownItems =
    data?.segmentSuggestions?.items.filter((segment) => !values.map(({ id }) => id).includes(segment.id)) || []

  const handleChange = (segments: GTSegmentSuggestion[], pattern = ''): void => {
    setPattern(pattern)
    onChange(segments)
  }

  return (
    <SelectTags
      placeholder={placeholder}
      values={values}
      label={label}
      onChange={handleChange}
      items={shownItems}
      getTagLabel={(segment: GTSegmentSuggestion): string => getSegmentLabel(segment)}
      loading={loading || isDebouncing}
      error={error}
      {...props}
    />
  )
}
